type SelectOption = {
  value?: number | string;
  label?: string;
  [key: string]: unknown;
};

type TruckQuestionValue = SelectOption | number | null | undefined;

type OrderUpdateFormValues = {
  id?: number;
  customer?: SelectOption | null;
  customer_id?: number | null;
  carrier?: SelectOption | null;
  carrier_id?: number | null;
  status?: SelectOption | number | null;
  po_number?: string;
  seal_number?: string | null;
  container_number?: string | null;
  expected_date?: string | Date | null;
  notes?: string | null;
  currency?: SelectOption | number | null;
  truck_question_1?: TruckQuestionValue;
  truck_question_2?: TruckQuestionValue;
  truck_question_3?: TruckQuestionValue;
  truck_question_4?: TruckQuestionValue;
};

type OrderItemUpdateValues = {
  unit_cost?: number | string | null;
  total_cost?: number | string | null;
  unit_type?: number | null;
};

type PriceSplitValues = {
  quantity?: number | string | null;
  unit_cost?: number | string | null;
};

function resolveSelectValue(
  value: SelectOption | number | null | undefined,
): number | null {
  if (value != null && typeof value === "object" && "value" in value) {
    const resolved = value.value;
    return resolved == null ? null : (resolved as number);
  }
  return typeof value === "number" ? value : null;
}

function resolveTruckQuestion(value: TruckQuestionValue): number | undefined {
  if (value != null && typeof value === "object" && "value" in value) {
    return value.value as number | undefined;
  }
  return typeof value === "number" ? value : undefined;
}

/**
 * Build the body for PUT /api/orders/{id}.
 * Omits form-only state (items, credit_returns, nested selects) so line
 * items are never serialized into order header updates.
 */
export function buildOrderUpdatePayload(values: OrderUpdateFormValues) {
  const customer_id =
    resolveSelectValue(values.customer) ?? values.customer_id ?? null;
  const carrier_id =
    resolveSelectValue(values.carrier) ?? values.carrier_id ?? null;
  const status = resolveSelectValue(
    typeof values.status === "object" || typeof values.status === "number"
      ? values.status
      : null,
  );

  const body: Record<string, unknown> = {
    id: values.id,
    customer_id,
    carrier_id,
    status,
    po_number: values.po_number,
    seal_number: values.seal_number ?? null,
    container_number: values.container_number ?? null,
    expected_date: values.expected_date ?? null,
    notes: values.notes ?? null,
    truck_question_1: resolveTruckQuestion(values.truck_question_1),
    truck_question_2: resolveTruckQuestion(values.truck_question_2),
    truck_question_3: resolveTruckQuestion(values.truck_question_3),
    truck_question_4: resolveTruckQuestion(values.truck_question_4),
  };

  // Omit currency when unset so the API can default to 1 (null is not equivalent).
  if (values.currency != null) {
    body.currency = values.currency;
  }

  return body;
}

/**
 * Build the body for PUT /api/orderItems/{id}.
 * Only pricing / unit fields are accepted by the API.
 */
export function buildOrderItemUpdatePayload(orderItem: OrderItemUpdateValues) {
  return {
    unit_cost: orderItem.unit_cost,
    total_cost: orderItem.total_cost,
    unit_type: orderItem.unit_type,
  };
}

/**
 * Build the body for POST/PUT price-split endpoints.
 */
export function buildPriceSplitPayload(payload: PriceSplitValues) {
  return {
    quantity: payload.quantity,
    unit_cost: payload.unit_cost,
  };
}
